import { useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { Card } from '../components/ui';

export function LoginPage() {
  const { user, loading, login, completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once the password is accepted but a second factor is still needed.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const codeInput = useRef<HTMLInputElement>(null);

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/dashboard'} replace />;
  }

  function goOnwards() {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from ?? '/dashboard', { replace: true });
  }

  async function onPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const outcome = await login(loginId, password);
      if (outcome.status === 'two-factor-required') {
        setChallengeToken(outcome.challengeToken);
        setPassword('');
        requestAnimationFrame(() => codeInput.current?.focus());
      } else {
        goOnwards();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  async function onCode(e: FormEvent) {
    e.preventDefault();
    if (!challengeToken) return;
    setBusy(true);
    setError(null);
    try {
      await completeTwoFactor(challengeToken, code);
      goOnwards();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      // An expired or exhausted challenge cannot be retried — restart the sign-in.
      if (err instanceof ApiError && err.status === 400) {
        setChallengeToken(null);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card style={{ maxWidth: 400, margin: '2rem auto' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginTop: 0, marginBottom: '1.5rem' }}>
        {challengeToken ? 'Two-factor authentication' : 'Sign in'}
      </h2>

      {error && <div className="alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {challengeToken ? (
        <form onSubmit={onCode}>
          <p className="muted" style={{ fontSize: '0.85rem', marginTop: 0 }}>
            Enter the 6-digit code from your authenticator app, or one of your backup codes.
          </p>
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label" htmlFor="code">Code</label>
            <input
              id="code"
              ref={codeInput}
              className="form-input mono"
              type="text"
              required
              autoComplete="one-time-code"
              inputMode="text"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={busy}>
            {busy ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            className="btn-ghost"
            style={{ width: '100%', marginTop: '0.5rem' }}
            onClick={() => { setChallengeToken(null); setCode(''); setError(null); }}
          >
            Start over
          </button>
        </form>
      ) : (
        <form onSubmit={onPassword}>
          <div style={{ marginBottom: '1rem' }}>
            {/* The login endpoint accepts either. */}
            <label className="form-label" htmlFor="login">Username or email</label>
            <input
              id="login"
              className="form-input"
              type="text"
              required
              autoComplete="username"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label" htmlFor="password">Password</label>
            <input
              id="password"
              className="form-input"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      {!challengeToken && (
        <p className="muted" style={{ marginTop: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
          No account? <Link to="/register">Sign up</Link>
        </p>
      )}
    </Card>
  );
}
